import { useState, useMemo } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import api from '@/lib/api'
import { Loader2, Play, TrendingUp, TrendingDown } from 'lucide-react'
import FundIdentityCard from '@/components/ui/FundIdentityCard'

type Fund = { id: string; name: string; ticker: string; source: string }
type FamilyInfo = { id: string; label: string }

type SubContrib = {
  factor: string
  beta_ortho: number | null
  beta_raw: number | null
  factor_return: number | null
  contribution: number | null
  t_stat: number | null
  significant: boolean
}

type SubWindow = {
  start: string
  end: string
  n_days?: number
  fund_actual?: number | null
  predicted?: number | null
  residual?: number | null
  factor_contributions?: SubContrib[]
  training?: {
    start: string
    end: string
    n_obs: number
    r_squared: number | null
    alpha_weekly: number | null
  }
  error?: string
}

type FactorSummary = {
  factor: string
  total_contribution: number | null
  avg_beta_ortho: number | null
  avg_beta_raw: number | null
  monthly_contributions: (number | null)[]
  monthly_betas_ortho: (number | null)[]
  monthly_factor_returns: (number | null)[]
  n_months: number
}

type AttributionResp = {
  run_id: number
  fund_id: string
  period: { key: string; label: string; start: string; end: string; n_days: number }
  history: string
  total_actual: number | null
  total_predicted: number | null
  total_residual: number | null
  sub_windows: SubWindow[]
  factor_summary: FactorSummary[]
  family_filter: string[] | null
  rolling_monthly: boolean
}

const PERIOD_OPTIONS: { key: string; label: string; hint?: string }[] = [
  { key: 'ytd',        label: 'YTD' },
  { key: 'current_dd', label: 'Current DD', hint: 'auto from latest HWM' },
  { key: 'max_dd',     label: 'Max DD',     hint: 'auto peak→trough' },
  { key: '1m',         label: '1M' },
  { key: '3m',         label: '3M' },
  { key: '6m',         label: '6M' },
  { key: '1y',         label: '1Y' },
  { key: '2y',         label: '2Y' },
  { key: 'custom',     label: 'Custom' },
]

export default function RiskAttribution() {
  const [search, setSearch] = useState('')
  const [fundId, setFundId] = useState('')
  const [periodKey, setPeriodKey] = useState('current_dd')
  const [history, setHistory] = useState('5y')
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')
  const [universe] = useState('narrow')
  const [topK, setTopK] = useState(15)
  const [families, setFamilies] = useState<string[]>(['equity'])

  const fundsQ = useQuery<Fund[]>({
    queryKey: ['fa-funds'],
    queryFn: () => api.get('/fund-analysis/funds').then(r => r.data),
  })
  const familiesQ = useQuery<FamilyInfo[]>({
    queryKey: ['factor-families'],
    queryFn: () => api.get('/risk-stats/factor-families').then(r => r.data),
  })

  const run = useMutation<AttributionResp, unknown>({
    mutationFn: () => api.post('/risk-stats/attribution', {
      fund_id: fundId,
      period_key: periodKey,
      history,
      custom_start: periodKey === 'custom' ? customStart : undefined,
      custom_end: periodKey === 'custom' ? customEnd : undefined,
      universe, top_k: topK,
      factor_families: families.length > 0 ? families : null,
    }).then(r => r.data),
  })

  const filteredFunds = useMemo(() => {
    if (!fundsQ.data) return []
    const s = search.toLowerCase()
    return fundsQ.data
      .filter(f => !s || f.name?.toLowerCase().includes(s) || f.ticker?.toLowerCase().includes(s))
      .slice(0, 300)
  }, [fundsQ.data, search])

  const pct = (v: number | null | undefined, d = 2) =>
    v == null ? '—' : `${(v * 100 >= 0 ? '+' : '')}${(v * 100).toFixed(d)}%`
  const num = (v: number | null | undefined, d = 3) => v == null ? '—' : v.toFixed(d)

  const data = run.data
  const canRun = fundId && (periodKey !== 'custom' || (customStart && customEnd))

  // Max absolute contribution for bar scaling
  const maxAbs = useMemo(() => {
    if (!data?.factor_summary) return 0.0001
    return Math.max(
      ...data.factor_summary.map(f => Math.abs(f.total_contribution ?? 0)),
      Math.abs(data.total_residual ?? 0),
      0.0001,
    )
  }, [data])

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Period Attribution</h1>
        <p className="text-muted-foreground text-sm">
          Out-of-sample attribution over a chosen period.
          Betas are estimated on <span className="font-mono">[period_start − history, period_start]</span> using
          weekly-overlapping 5-day returns, and re-estimated monthly when the period exceeds one month.
          Residual = actual − Σ β × factor return.
        </p>
      </div>

      {/* Fund selector */}
      <div className="bg-card border border-border rounded-lg p-4 space-y-4">
        <div className="flex flex-col md:flex-row gap-3">
          <input
            type="text"
            placeholder="Search by name or ticker..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="input flex-1"
          />
          <select
            value={fundId}
            onChange={e => setFundId(e.target.value)}
            className="input w-full md:w-[28rem]"
          >
            <option value="">-- Select fund --</option>
            {filteredFunds.map(f => (
              <option key={f.id} value={f.id}>{f.name} ({f.ticker}) [{f.source}]</option>
            ))}
          </select>
        </div>

        {/* Period buttons */}
        <div>
          <div className="text-xs uppercase text-muted-foreground mb-1">Period to explain</div>
          <div className="flex flex-wrap gap-2">
            {PERIOD_OPTIONS.map(p => (
              <button
                key={p.key}
                type="button"
                onClick={() => setPeriodKey(p.key)}
                title={p.hint}
                className={`px-3 py-1.5 text-sm rounded border transition-colors ${
                  periodKey === p.key
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'border-border hover:bg-muted'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {periodKey === 'custom' && (
            <div className="flex flex-wrap gap-2 mt-2 items-center">
              <label className="text-sm flex items-center gap-2">
                <span className="text-muted-foreground">From</span>
                <input type="date" className="input" value={customStart}
                       onChange={e => setCustomStart(e.target.value)} />
              </label>
              <label className="text-sm flex items-center gap-2">
                <span className="text-muted-foreground">To</span>
                <input type="date" className="input" value={customEnd}
                       onChange={e => setCustomEnd(e.target.value)} />
              </label>
            </div>
          )}
        </div>

        {/* History + other options */}
        <div className="flex flex-wrap gap-3 items-center border-t border-border pt-3">
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">History</span>
            <select value={history} onChange={e => setHistory(e.target.value)} className="input">
              <option value="2y">2Y rolling</option>
              <option value="5y">5Y rolling</option>
              <option value="itd">ITD (inception → period start)</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Top K</span>
            <input
              type="number"
              value={topK}
              onChange={e => setTopK(Math.max(3, Math.min(50, Number(e.target.value) || 15)))}
              className="input w-20"
            />
          </label>
          <button
            type="button"
            disabled={!canRun || run.isPending}
            onClick={() => run.mutate()}
            className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {run.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Run attribution
          </button>
        </div>

        {/* Family filter pills */}
        <div className="border-t border-border pt-3">
          <div className="flex items-center gap-2 text-sm mb-2">
            <span className="font-medium">Factor families</span>
            <span className="text-xs text-muted-foreground">(empty = all)</span>
            <button type="button" onClick={() => setFamilies([])}
                    className="ml-auto text-xs text-muted-foreground hover:text-foreground underline">
              clear
            </button>
            <button type="button" onClick={() => setFamilies(['equity'])}
                    className="text-xs text-muted-foreground hover:text-foreground underline">
              equity-only
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {(familiesQ.data ?? []).map(f => {
              const active = families.includes(f.id)
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() =>
                    setFamilies(active
                      ? families.filter(x => x !== f.id)
                      : [...families, f.id])
                  }
                  className={`px-2.5 py-1 text-xs rounded border transition-colors ${
                    active
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-border text-muted-foreground hover:bg-muted'
                  }`}
                >{f.label}</button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Fund identity card */}
      {fundId && <FundIdentityCard fundId={fundId} />}

      {/* Error */}
      {run.isError && (
        <div className="bg-destructive/10 border border-destructive text-destructive text-sm rounded p-3">
          {String((run.error as any)?.response?.data?.detail || (run.error as Error)?.message || 'Attribution failed')}
        </div>
      )}

      {/* Results */}
      {data && (
        <>
          {/* Headline cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Card label="Period" value={data.period.label}
                  subtitle={`${data.period.start} → ${data.period.end} · ${data.period.n_days}d`} />
            <Card label="Fund actual" value={pct(data.total_actual)}
                  color={(data.total_actual ?? 0) >= 0 ? 'positive' : 'negative'} />
            <Card label="Predicted by style" value={pct(data.total_predicted)}
                  subtitle={data.rolling_monthly ? 'rolling monthly' : 'single window'}
                  color={(data.total_predicted ?? 0) >= 0 ? 'positive' : 'negative'} />
            <Card label="Residual (unexplained α)" value={pct(data.total_residual)}
                  subtitle={`history: ${data.history}`}
                  emphasis
                  color={(data.total_residual ?? 0) >= 0 ? 'positive' : 'negative'} />
          </div>

          {/* Per-month breakdown */}
          {data.sub_windows.length > 1 && (
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-sm font-medium mb-2">Monthly breakdown (re-estimated each month)</div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-muted-foreground border-b border-border">
                    <th className="text-left py-2">Start → End</th>
                    <th className="text-right">Actual</th>
                    <th className="text-right">Predicted</th>
                    <th className="text-right">Residual</th>
                    <th className="text-right">R²</th>
                    <th className="text-right">Train window</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sub_windows.map((sw, i) => (
                    sw.error ? (
                      <tr key={i} className="border-b border-border/60">
                        <td colSpan={6} className="py-2 text-destructive text-xs">
                          {sw.start} → {sw.end}: {sw.error}
                        </td>
                      </tr>
                    ) : (
                      <tr key={i} className="border-b border-border/60">
                        <td className="py-1.5 font-mono text-xs">{sw.start} → {sw.end}</td>
                        <td className={`text-right font-mono ${(sw.fund_actual ?? 0) < 0 ? 'text-destructive' : 'text-emerald-600'}`}>
                          {pct(sw.fund_actual)}
                        </td>
                        <td className="text-right font-mono">{pct(sw.predicted)}</td>
                        <td className={`text-right font-mono font-medium ${(sw.residual ?? 0) < 0 ? 'text-destructive' : 'text-emerald-600'}`}>
                          {pct(sw.residual)}
                        </td>
                        <td className="text-right font-mono text-xs">{num(sw.training?.r_squared, 2)}</td>
                        <td className="text-right font-mono text-xs text-muted-foreground">
                          {sw.training?.start}..{sw.training?.end} · n={sw.training?.n_obs}
                        </td>
                      </tr>
                    )
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Waterfall / per-factor contributions */}
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="text-sm font-medium mb-3">
              Per-factor contributions
              <span className="text-xs text-muted-foreground ml-2">
                (orthogonalised β × period factor return, aggregated across
                {data.rolling_monthly ? ` ${data.sub_windows.length} monthly fits` : ' single fit'})
              </span>
            </div>
            <div className="space-y-1">
              {data.factor_summary.map(f => {
                const pctBar = Math.abs((f.total_contribution ?? 0) / maxAbs) * 100
                const isNeg = (f.total_contribution ?? 0) < 0
                return (
                  <div key={f.factor} className="flex items-center gap-2 text-xs">
                    <div className="w-56 truncate font-mono" title={f.factor}>{f.factor}</div>
                    <div className="flex-1 h-4 bg-muted relative rounded">
                      <div
                        className={`absolute top-0 h-4 ${isNeg ? 'bg-destructive/70 right-1/2' : 'bg-emerald-600/70 left-1/2'}`}
                        style={{ width: `${pctBar / 2}%` }}
                      />
                      <div className="absolute left-1/2 top-0 h-4 w-px bg-border" />
                    </div>
                    <div className={`w-20 text-right font-mono ${isNeg ? 'text-destructive' : 'text-emerald-600'}`}>
                      {pct(f.total_contribution)}
                    </div>
                    <div className="w-20 text-right font-mono text-muted-foreground">
                      β̄ {num(f.avg_beta_ortho, 2)}
                    </div>
                    <div className="w-10 text-right text-muted-foreground">
                      {f.n_months}m
                    </div>
                  </div>
                )
              })}
              {/* Residual row */}
              <div className="flex items-center gap-2 text-xs pt-2 border-t border-border mt-2">
                <div className="w-56 font-medium">Unexplained residual (α)</div>
                <div className="flex-1 h-4 bg-muted relative rounded">
                  <div
                    className={`absolute top-0 h-4 ${(data.total_residual ?? 0) < 0 ? 'bg-destructive right-1/2' : 'bg-emerald-600 left-1/2'}`}
                    style={{ width: `${Math.min(100, Math.abs((data.total_residual ?? 0) / maxAbs) * 50)}%` }}
                  />
                  <div className="absolute left-1/2 top-0 h-4 w-px bg-border" />
                </div>
                <div className={`w-20 text-right font-mono font-semibold ${(data.total_residual ?? 0) < 0 ? 'text-destructive' : 'text-emerald-600'}`}>
                  {pct(data.total_residual)}
                </div>
                <div className="w-20" />
                <div className="w-10" />
              </div>
            </div>
          </div>

          {/* Assessment footer */}
          <div className="bg-card/50 border border-border rounded-lg p-4 text-sm">
            <div className="flex items-start gap-3">
              {Math.abs(data.total_residual ?? 0) > Math.abs(data.total_actual ?? 0) * 0.5 ? (
                <TrendingDown className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              ) : (
                <TrendingUp className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
              )}
              <div>
                <div className="font-medium">
                  {(() => {
                    const ratio = Math.abs(data.total_residual ?? 0) / Math.max(Math.abs(data.total_actual ?? 0), 1e-6)
                    if (ratio < 0.3) return 'Style factors explain most of the period return.'
                    if (ratio < 0.6) return 'Meaningful residual — partially style, partially idiosyncratic.'
                    return 'Most of the period return is idiosyncratic (not explained by style exposures).'
                  })()}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Training history: <span className="font-mono">{data.history.toUpperCase()}</span>
                  {data.family_filter && <> · Families: <span className="font-mono">{data.family_filter.join(', ')}</span></>}
                  {data.rolling_monthly && ` · ${data.sub_windows.length} monthly re-estimations`}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function Card({
  label, value, subtitle, color, emphasis,
}: {
  label: string
  value: string
  subtitle?: string
  color?: 'positive' | 'negative'
  emphasis?: boolean
}) {
  const valueColor =
    color === 'negative' ? 'text-destructive' :
    color === 'positive' ? 'text-emerald-600' :
    ''
  return (
    <div className={`bg-card border rounded-lg p-4 ${emphasis ? 'border-primary' : 'border-border'}`}>
      <div className="text-xs uppercase text-muted-foreground">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${valueColor}`}>{value}</div>
      {subtitle && <div className="text-xs text-muted-foreground mt-1">{subtitle}</div>}
    </div>
  )
}
